import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Plus, LogOut, ChevronLeft } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';
import { SavedServer, ServerListItem, Championship, Car, Track } from '../types';
import ServerList from '../components/ServerList';
import ContentTabs from '../components/ContentTabs';
import ChampionshipList from '../components/ChampionshipList';
import ChampionshipEditor from '../components/ChampionshipEditor';
import WebsiteConfig from '../components/WebsiteConfig';
import LogReplayTool from '../components/LogReplayTool';
import DiscordBot from '../components/DiscordBot';
import './DashboardPage.css';

const VALID_SERVER_TABS = ['config', 'logs', 'results', 'stewarding', 'json', 'live'] as const;
type ServerTab = typeof VALID_SERVER_TABS[number];
const VALID_CHAMPIONSHIP_TABS = ['details', 'events', 'entrylist', 'results'] as const;
type ChampionshipTab = typeof VALID_CHAMPIONSHIP_TABS[number];

interface ServerEditorRouteProps {
  existingPorts: number[];
  onSave: (server: SavedServer) => Promise<void>;
  onRefreshServer: (serverId: string) => Promise<SavedServer | null>;
  onClose: () => void;
}

const ServerEditorRoute: React.FC<ServerEditorRouteProps> = ({ existingPorts, onSave, onRefreshServer, onClose }) => {
  const { serverId, tab } = useParams<{ serverId: string; tab?: string }>();
  const navigate = useNavigate();
  const [server, setServer] = useState<SavedServer | null>(null);
  const [serverLoading, setServerLoading] = useState(false);
  const isNew = serverId === 'new';
  const activeTab = (VALID_SERVER_TABS.includes(tab as ServerTab) ? tab : 'config') as ServerTab;

  useEffect(() => {
    if (isNew || !serverId) return;
    setServer(null);
    const load = async () => {
      setServerLoading(true);
      try {
        const res = await api.getServer(serverId);
        setServer(res.data);
      } catch {
        navigate('/servers');
      } finally {
        setServerLoading(false);
      }
    };
    load();
  }, [serverId, isNew, navigate]);

  if (!isNew && serverLoading) {
    return <div className="no-selection">Loading server...</div>;
  }

  return (
    <ContentTabs
      server={isNew ? null : server}
      isNew={isNew}
      existingPorts={existingPorts}
      activeTab={activeTab}
      onTabChange={(newTab) => navigate(`/servers/${serverId}/${newTab}`)}
      onSave={onSave}
      onClose={onClose}
      onRefreshServer={async (id) => {
        const updated = await onRefreshServer(id);
        if (updated) setServer(updated);
      }}
    />
  );
};

interface ChampionshipEditorRouteProps {
  championships: Championship[];
  cars: Car[];
  tracks: Track[];
  onSave: (championship: Championship) => Promise<void>;
  onCancel: () => void;
}

const ChampionshipEditorRoute: React.FC<ChampionshipEditorRouteProps> = ({ championships, cars, tracks, onSave, onCancel }) => {
  const { championshipId, tab } = useParams<{ championshipId: string; tab?: string }>();
  const navigate = useNavigate();
  const isNew = championshipId === 'new';
  const championship = championships.find((c) => c.id === championshipId) || null;
  const activeTab = (VALID_CHAMPIONSHIP_TABS.includes(tab as ChampionshipTab) ? tab : 'details') as ChampionshipTab;

  if (!isNew && !championship && championships.length > 0) {
    return <Navigate to="/championships" replace />;
  }

  return (
    <ChampionshipEditor
      championship={isNew ? null : championship}
      isNew={isNew}
      cars={cars}
      tracks={tracks}
      activeTab={activeTab}
      onTabChange={(newTab) => navigate(`/championships/${championshipId}/${newTab}`)}
      onSave={onSave}
      onCancel={onCancel}
    />
  );
};

const DashboardPage: React.FC = () => {
  const [servers, setServers] = useState<SavedServer[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showMobilePanel, setShowMobilePanel] = useState<'servers' | 'editor'>('servers');
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const [broadcastPlatform, setBroadcastPlatform] = useState<'youtube' | 'twitch' | ''>('');
  const [broadcastChannel, setBroadcastChannel] = useState('');
  const [enableLogReplay, setEnableLogReplay] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);

  const [championships, setChampionships] = useState<Championship[]>([]);
  const [isCreatingChampionship, setIsCreatingChampionship] = useState(false);
  const [championshipLoading, setChampionshipLoading] = useState(false);
  const [cars, setCars] = useState<Car[]>([]);
  const [tracks, setTracks] = useState<Track[]>([]);

  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const activeView = location.pathname.startsWith('/championships') ? 'championships'
    : location.pathname.startsWith('/website-config') ? 'results'
    : location.pathname.startsWith('/discord-bot') ? 'discord'
    : location.pathname.startsWith('/log-replay') ? 'debug'
    : 'servers';

  const selectedServerId = location.pathname.match(/^\/servers\/([^/]+)/)?.[1];
  const selectedChampionshipId = location.pathname.match(/^\/championships\/([^/]+)/)?.[1];

  const isEditorOpen =
    (activeView === 'servers' && (isCreating || Boolean(selectedServerId))) ||
    (activeView === 'championships' && (isCreatingChampionship || Boolean(selectedChampionshipId)));

  useEffect(() => {
    const isTouchEnabled = () => (
      window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
      ('ontouchstart' in window) ||
      navigator.maxTouchPoints > 0
    );
    setIsTouchDevice(isTouchEnabled());

    const mq = window.matchMedia('(max-width: 900px)');
    setIsNarrowViewport(mq.matches);
    const onMqChange = (e: MediaQueryListEvent) => setIsNarrowViewport(e.matches);
    mq.addEventListener('change', onMqChange);
    return () => mq.removeEventListener('change', onMqChange);
  }, []);

  // Sync panel view with URL on narrow screens (direct load / browser back)
  useEffect(() => {
    if (isEditorOpen) {
      setShowMobilePanel('editor');
    } else {
      setShowMobilePanel('servers');
    }
  }, [isEditorOpen]);

  useEffect(() => {
    loadServers();
    loadChampionships();
    loadBroadcastConfig();
    const interval = setInterval(loadServers, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activeView === 'championships') {
      loadCarsAndTracks();
    }
    // Refresh config (e.g. enableLogReplay) whenever user navigates away from Website Config
    loadBroadcastConfig();
  }, [activeView]);

  const loadBroadcastConfig = async () => {
    try {
      const res = await api.getWebsiteConfig();
      const cfg = res.data?.config || {};
      if (cfg.broadcastPlatform) setBroadcastPlatform(cfg.broadcastPlatform);
      if (cfg.broadcastChannel) setBroadcastChannel(cfg.broadcastChannel);
      setEnableLogReplay(cfg.enableLogReplay === true);
    } catch {
      // non-critical
    }
  };

  const loadChampionships = async () => {
    try {
      setChampionshipLoading(true);
      const response = await api.getChampionships();
      setChampionships(response.data.championships || []);
    } catch (err) {
      console.error('Failed to load championships:', err);
    } finally {
      setChampionshipLoading(false);
    }
  };

  const loadCarsAndTracks = async () => {
    try {
      const [carsRes, tracksRes] = await Promise.all([api.getCars(), api.getRaceTracks()]);
      setCars(carsRes.data || []);
      setTracks(tracksRes.data || []);
    } catch (err) {
      console.error('Failed to load cars/tracks:', err);
    }
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX;
      const newWidth = Math.max(200, Math.min(600, resizeStartWidth + delta));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeStartX, resizeStartWidth]);

  const loadServers = async () => {
    try {
      const response = await api.getServers();
      setServers(response.data);
    } catch (err) {
      console.error('Failed to load servers:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshServer = async (serverId: string): Promise<SavedServer | null> => {
    try {
      const [listRes, fullRes] = await Promise.all([api.getServers(), api.getServer(serverId)]);
      setServers(listRes.data);
      return fullRes.data as SavedServer;
    } catch (err) {
      console.error('Failed to refresh server:', err);
      return null;
    }
  };

  const handleLogout = () => {
    logout();
    api.clearToken();
  };

  const handleServerSelect = (server: ServerListItem) => {
    navigate(`/servers/${server.id}`);
    setShowMobilePanel('editor');
    setIsCreating(false);
  };

  const handleNewServer = () => {
    setIsCreating(true);
    setShowMobilePanel('editor');
    navigate('/servers/new');
  };

  const handleBackToServers = () => {
    setIsCreating(false);
    setShowMobilePanel('servers');
    navigate('/servers');
  };

  const handleSave = async (server: SavedServer) => {
    try {
      if (isCreating) {
        await api.createServer({
          name: server.name,
          serverConfig: server.serverConfig,
          seasonConfig: server.seasonConfig,
          scheduledStart: server.scheduledStart,
        });
      } else {
        await api.updateServer(server.id, {
          name: server.name,
          serverConfig: server.serverConfig,
          seasonConfig: server.seasonConfig,
          scheduledStart: server.scheduledStart,
        });
      }
      await loadServers();
      setIsCreating(false);
      setShowMobilePanel('servers');
      navigate('/servers');
    } catch (err) {
      console.error('Failed to save server:', err);
    }
  };

  const handleLaunch = async (serverId: string) => {
    try {
      await api.launchServer(serverId);
      await loadServers();
    } catch (err: any) {
      alert(`Failed to launch server: ${err.response?.data?.error}`);
    }
  };

  const handleStop = async (serverId: string) => {
    try {
      await api.stopServer(serverId);
      await loadServers();
    } catch (err: any) {
      alert(`Failed to stop server: ${err.response?.data?.error}`);
    }
  };

  const handleDelete = async (serverId: string) => {
    if (!window.confirm('Are you sure you want to delete this server? This cannot be undone.')) return;
    try {
      await api.deleteServer(serverId);
      await loadServers();
      navigate('/servers');
    } catch (err: any) {
      alert(`Failed to delete server: ${err.response?.data?.error}`);
    }
  };

  const handleChampionshipSelect = (championship: Championship) => {
    navigate(`/championships/${championship.id}`);
    setIsCreatingChampionship(false);
    setShowMobilePanel('editor');
  };

  const handleNewChampionship = () => {
    setIsCreatingChampionship(true);
    setShowMobilePanel('editor');
    navigate('/championships/new');
  };

  const handleChampionshipCancel = () => {
    setIsCreatingChampionship(false);
    setShowMobilePanel('servers');
    navigate('/championships');
  };

  const handleChampionshipSave = async (championship: Championship) => {
    try {
      if (isCreatingChampionship) {
        const res = await api.createChampionship(championship);
        const created: Championship = res.data.championship || res.data;
        await loadChampionships();
        setIsCreatingChampionship(false);
        setShowMobilePanel('servers');
        navigate(`/championships/${created.id}`);
      } else {
        await api.updateChampionship(championship.id, championship);
        await loadChampionships();
        setIsCreatingChampionship(false);
        setShowMobilePanel('servers');
        navigate('/championships');
      }
    } catch (err: any) {
      console.error('Failed to save championship:', err);
      alert(`Failed to save championship: ${err.response?.data?.error || 'Unknown error'}`);
    }
  };

  const handleChampionshipDelete = async (championshipId: string) => {
    if (!window.confirm('Are you sure you want to delete this championship? This cannot be undone.')) return;
    try {
      await api.deleteChampionship(championshipId);
      await loadChampionships();
      navigate('/championships');
    } catch (err: any) {
      alert(`Failed to delete championship: ${err.response?.data?.error}`);
    }
  };

  const resizeHandle = !isTouchDevice && !isNarrowViewport ? (
    <div
      className="sidebar-divider"
      onMouseDown={(e) => {
        setIsResizing(true);
        setResizeStartX(e.clientX);
        setResizeStartWidth(sidebarWidth);
      }}
      title="Drag to resize sidebar"
    />
  ) : null;

  return (
    <div className={`dashboard ${isTouchDevice ? 'is-mobile' : ''}`}>
      <div className="dashboard-header">
        <div className="header-content">
          {isEditorOpen ? (
            <button
              onClick={() => activeView === 'servers' ? handleBackToServers() : handleChampionshipCancel()}
              className="back-button"
            >
              <ChevronLeft size={20} />
            </button>
          ) : null}
          <h1>AC EVO Server Configurator</h1>
          <button onClick={handleLogout} className="logout-button">
            <LogOut size={18} />
            <span className="button-text">Logout</span>
          </button>
        </div>
      </div>

      <div className="dashboard-nav">
        <button
          className={`nav-button ${activeView === 'servers' ? 'active' : ''}`}
          onClick={() => { navigate('/servers'); setShowMobilePanel('servers'); }}
        >
          Server Configurator
        </button>
        <button
          className={`nav-button ${activeView === 'championships' ? 'active' : ''}`}
          onClick={() => { navigate('/championships'); setShowMobilePanel('servers'); }}
        >
          Championship Manager
        </button>
        <button
          className={`nav-button ${activeView === 'results' ? 'active' : ''}`}
          onClick={() => { navigate('/website-config'); setShowMobilePanel('servers'); }}
        >
          Website Config
        </button>
        <button
          className={`nav-button ${activeView === 'discord' ? 'active' : ''}`}
          onClick={() => { navigate('/discord-bot'); setShowMobilePanel('servers'); }}
        >
          Discord Bot
        </button>
        {enableLogReplay && (
          <button
            className={`nav-button ${activeView === 'debug' ? 'active' : ''}`}
            onClick={() => navigate('/log-replay')}
          >
            Log Replay
          </button>
        )}
      </div>

      <Routes>
        <Route path="/" element={<Navigate to="/servers" replace />} />

        <Route path="/servers/*" element={
          <div className={`dashboard-container ${showMobilePanel === 'servers' ? 'show-servers' : 'show-editor'}`}>
            <div className="servers-panel" style={{ width: !isTouchDevice && !isNarrowViewport ? `${sidebarWidth}px` : undefined }}>
              <div className="servers-header">
                <h2>Servers</h2>
                <button onClick={handleNewServer} className="new-server-button">
                  <Plus size={18} />
                  <span className="button-text">New Server</span>
                </button>
              </div>
              {loading ? (
                <div className="loading">Loading servers...</div>
              ) : (
                <ServerList
                  servers={servers}
                  selectedId={selectedServerId && selectedServerId !== 'new' ? selectedServerId : undefined}
                  championships={championships}
                  broadcastPlatform={broadcastPlatform}
                  broadcastChannel={broadcastChannel}
                  onSelect={handleServerSelect}
                  onLaunch={handleLaunch}
                  onStop={handleStop}
                  onDelete={handleDelete}
                />
              )}
            </div>

            {resizeHandle}

            <div className="editor-panel">
              <Routes>
                <Route path=":serverId/:tab?" element={
                  <ServerEditorRoute
                    existingPorts={servers.map((s) => s.serverConfig.server_tcp_listener_port).filter(Boolean)}
                    onSave={handleSave}
                    onRefreshServer={refreshServer}
                    onClose={handleBackToServers}
                  />
                } />
                <Route path="" element={
                  <ContentTabs server={null} isNew={false} onSave={handleSave} onClose={handleBackToServers} />
                } />
              </Routes>
            </div>
          </div>
        } />

        <Route path="/championships/*" element={
          <div className={`dashboard-container ${showMobilePanel === 'servers' ? 'show-servers' : 'show-editor'}`}>
            <div className="servers-panel" style={{ width: !isTouchDevice && !isNarrowViewport ? `${sidebarWidth}px` : undefined }}>
              <div className="servers-header">
                <h2>Championships</h2>
                <button onClick={handleNewChampionship} className="new-server-button">
                  <Plus size={18} />
                  <span className="button-text">New</span>
                </button>
              </div>
              {championshipLoading ? (
                <div className="loading">Loading championships...</div>
              ) : (
                <ChampionshipList
                  championships={championships}
                  selectedId={selectedChampionshipId && selectedChampionshipId !== 'new' ? selectedChampionshipId : null}
                  onSelect={handleChampionshipSelect}
                  onDelete={handleChampionshipDelete}
                />
              )}
            </div>

            {resizeHandle}

            <div className="editor-panel">
              <Routes>
                <Route path=":championshipId/:tab?" element={
                  <ChampionshipEditorRoute
                    championships={championships}
                    cars={cars}
                    tracks={tracks}
                    onSave={handleChampionshipSave}
                    onCancel={handleChampionshipCancel}
                  />
                } />
                <Route path="" element={
                  <ChampionshipEditor
                    championship={null}
                    isNew={false}
                    cars={cars}
                    tracks={tracks}
                    onSave={handleChampionshipSave}
                    onCancel={handleChampionshipCancel}
                  />
                } />
              </Routes>
            </div>
          </div>
        } />

        <Route path="/website-config" element={
          <div className="results-explorer-panel"><WebsiteConfig /></div>
        } />

        <Route path="/discord-bot" element={
          <div className="results-explorer-panel"><DiscordBot /></div>
        } />

        <Route path="/log-replay" element={
          enableLogReplay
            ? <div className="results-explorer-panel"><LogReplayTool servers={servers} /></div>
            : <Navigate to="/servers" replace />
        } />
      </Routes>
    </div>
  );
};

export default DashboardPage;
